import { Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';
import { featureFlagsSelectors } from '../feature-flags';

import * as entitlementsTypes from './entitlementsTypes';

const appTiers = {
  ACME_PROVISIONAL: 1,
  ACME_BASIC: 2,
  ACME_PREMIER: 4,
  ACME_HAB: 5,
};

const getStore = (state) => state[entitlementsTypes.ENTITLEMENTS_REDUCER_KEY];
export const getLoadPending = (state) => getStore(state).loadPending;
export const getError = (state) => getStore(state).error;
export const getIsLoading = (state) => getStore(state).isLoading;
export const getEntitlements = (state) => getStore(state).resourcesById;
export const getResourcesById = (state) => getStore(state).resourcesById;

export const getActiveEntitlements = (function createActiveEntitlementsSelector() {
  let activeEntitlementsCache = ImmutableMap();
  return (state) => {
    const now = DateTime.local();
    const activeEntitlementsNew = getEntitlements(state).filter((entitlement) => {
      const partnerExpirationDate = entitlement.partnerExpireAt && DateTime.fromISO(entitlement.partnerExpireAt); // Set for only appstore/playstore subscriptions.
      // If there is partnerExpirationDate, only use that to check subscription status to support sandbox testing.
      if (partnerExpirationDate) {
        if (now <= partnerExpirationDate) {
          return true;
        }
        // 'expiresAt' is deprecated, but we have to use it for scenario when user switched from partner to e-store
        // "expiresAt": "2025-08-02T07:59:59Z",
        // "partnerExpireAt": "2019-11-17T00:00:00Z",
        // "expiresOn": "2025-08-01",
        if (entitlement.expiresAt) {
          return now <= DateTime.fromISO(entitlement.expiresAt);
        }
      }
      return entitlement.active && now.startOf('day') <= DateTime.fromISO(entitlement.expiresOn);
    });
    const cacheMatches = activeEntitlementsCache.equals(activeEntitlementsNew);
    if (!cacheMatches) {
      activeEntitlementsCache = activeEntitlementsNew;
    }
    return activeEntitlementsCache;
  };
})();

export const getActiveEntitlement = (state, key) => getActiveEntitlements(state).get(key);

export const getActiveTierEntitlements = createSelector(getActiveEntitlements, (activeEntitlements) =>
  activeEntitlements.filter((entitlement) => Object.prototype.hasOwnProperty.call(appTiers, entitlement.id)),
);

export const getTopTierEntitlement = createSelector(getActiveTierEntitlements, (entitlements) =>
  entitlements
    .sort((entitlement1, entitlement2) =>
      Math.sign((appTiers[entitlement1.id] || 0) - (appTiers[entitlement2.id] || 0)),
    )
    .last(),
);

export const getBusinessEnabled = createSelector(
  [getTopTierEntitlement, (state) => featureFlagsSelectors.getFeatureFlag(state, 'businessEnabled')],
  (entitlement, businessEnabled) => businessEnabled || (entitlement?.active && entitlement?.id === 'ACME_HAB'),
);
